import graphql from "babel-plugin-relay/macro";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { readInlineData } from "react-relay";

import {
  getAggregatedOrderFinancialSummary,
  readOrderFinancialSummary,
} from "../../../ChargesSummary/hooks/getAggregatedOrderFinancialSummary";
import type {
  BookingType,
  DepartmentType,
  PromoCodeCategory,
  PromoCodeDiscountType,
  promoCodeAnalytics_booking$key,
} from "./__generated__/promoCodeAnalytics_booking.graphql";

export type PromoCodeAnalyticsContext = {
  department: string;
  facility: string;
};

export type AppliedPromoCodeAnalyticsData = PromoCodeAnalyticsContext & {
  promoCode: string;
  discountType: PromoCodeDiscountType;
  discountValue: number;
  subtotalBeforeDiscount: number;
  totalAfterDiscount: number;
  daysUntilBooking: number;
};

export type PromoCodeEvents = {
  PromoCodeApplied: AppliedPromoCodeAnalyticsData;
  PromoCodeRemoved: Pick<
    AppliedPromoCodeAnalyticsData,
    "department" | "facility" | "promoCode" | "discountType" | "discountValue"
  >;
  PromoCodeNotAccepted: PromoCodeAnalyticsContext & {
    promoCodeEntered: string;
    errorGiven: string;
  };
  PromoCodeRedeemed: AppliedPromoCodeAnalyticsData;
  PromoCodeEntryOpened: PromoCodeAnalyticsContext;
};

const getPromoCodeCategoryName = (category: PromoCodeCategory) => {
  switch (category) {
    case "ACTIVITY_MANAGEMENT":
      return "Activity Management";
    case "GOLF":
      return "Golf";
    case "LESSON":
      return "Lesson";
    default:
      return "Unknown";
  }
};

const getBookingDepartmentName = (
  bookingType: BookingType,
  departmentType: DepartmentType | null | undefined
) => {
  if (bookingType === "LESSON") {
    return "Lesson";
  }

  return departmentType === "GOLF" ? "Golf" : "Activity Management";
};

export const getPromoCodeAnalyticsContext = (
  bookingRef: promoCodeAnalytics_booking$key | null | undefined
): PromoCodeAnalyticsContext => {
  if (!bookingRef) {
    return { department: "Unknown", facility: "Unknown" };
  }

  const booking = readInlineData(
    promoCodeAnalyticsBookingFragmentString,
    bookingRef
  );
  const facility = booking.startSlot?.agenda?.facility;

  return {
    department: getBookingDepartmentName(
      booking.type,
      facility?.department?.type
    ),
    facility: facility?.name ?? "Unknown",
  };
};

export const getAppliedPromoCodeAnalyticsData = (
  bookingRef: promoCodeAnalytics_booking$key | null | undefined
): AppliedPromoCodeAnalyticsData | null => {
  if (!bookingRef) {
    return null;
  }

  const booking = readInlineData(
    promoCodeAnalyticsBookingFragmentString,
    bookingRef
  );
  const promoCode = booking.appliedPromoCode;
  const facility = booking.startSlot?.agenda?.facility;
  const bookingDate = booking.startSlot?.agenda?.date;

  if (!promoCode || !facility || !bookingDate) {
    return null;
  }

  const orderFinancialSummary = getAggregatedOrderFinancialSummary(
    booking.promoCodeOrders?.edges?.map((edge) =>
      readOrderFinancialSummary(edge?.node)
    ) ?? []
  );

  return {
    ...getPromoCodeAnalyticsContext(bookingRef),
    department: getPromoCodeCategoryName(promoCode.category),
    promoCode: promoCode.code,
    discountType: promoCode.discountType,
    discountValue:
      promoCode.discountType === "FIXED"
        ? promoCode.discountAmount / 100
        : promoCode.discountAmount,
    subtotalBeforeDiscount: orderFinancialSummary.subtotalBeforeDiscount / 100,
    totalAfterDiscount: orderFinancialSummary.totalAfterDiscount / 100,
    daysUntilBooking: differenceInCalendarDays(
      parseISO(bookingDate),
      new Date()
    ),
  };
};

export const promoCodeAnalyticsBookingFragmentString = graphql`
  fragment promoCodeAnalytics_booking on Booking @inline {
    type
    appliedPromoCode {
      code
      category
      discountType
      discountAmount
    }
    startSlot {
      agenda {
        date
        facility {
          name
          department {
            type
          }
        }
      }
    }
    promoCodeOrders: orders(first: 20, active: true) {
      edges {
        node {
          ...getAggregatedOrderFinancialSummary_order
        }
      }
    }
  }
`;
